import {
  CalendarDays,
  CheckSquare2,
  ChevronDown,
  CircleDollarSign,
  ClipboardList,
  CookingPot,
  HeartHandshake,
  Home,
  Inbox,
  Menu,
  MessageCircleMore,
  Moon,
  Plus,
  Settings,
  Sparkles,
  Sun,
  Users,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { Avatar } from "./Avatar";

interface NavigationItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const navigation: NavigationItem[] = [
  { to: "/", label: "Today", icon: Home, end: true },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/people", label: "People", icon: Users },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/tasks", label: "Tasks", icon: CheckSquare2 },
  { to: "/rhythms", label: "Rhythms", icon: Sparkles },
  { to: "/home", label: "Home", icon: ClipboardList },
  { to: "/money", label: "Money", icon: CircleDollarSign },
  { to: "/meals", label: "Meals", icon: CookingPot },
  { to: "/projects", label: "Projects", icon: ClipboardList },
  { to: "/us", label: "Us", icon: HeartHandshake },
  { to: "/assistant", label: "Chief of Staff", icon: MessageCircleMore },
];

const mobileNavigation = navigation.slice(0, 5);

interface AppShellProps {
  children: ReactNode;
  onCapture: () => void;
}

export function AppShell({ children, onCapture }: AppShellProps) {
  const [dark, setDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
  }, [dark]);

  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="Primary navigation">
        <div className="brand">
          <img src="/fam-mark.svg" alt="" />
          <span>Fam</span>
        </div>
        <nav className="side-nav">
          {navigation.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => (isActive ? "active" : undefined)}>
              <Icon size={19} strokeWidth={1.8} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <NavLink to="/settings">
            <Settings size={19} strokeWidth={1.8} />
            <span>Settings</span>
          </NavLink>
          <button className="profile-button" type="button">
            <Avatar initials="SC" color="forest" size="small" />
            <span><strong>Seth</strong><small>Administrator</small></span>
            <ChevronDown size={16} />
          </button>
        </div>
      </aside>

      <header className="mobile-header">
        <button className="icon-button" type="button" aria-label="Open menu" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <Menu size={22} />
        </button>
        <div className="brand"><img src="/fam-mark.svg" alt="" /><span>Fam</span></div>
        <button className="icon-button" type="button" aria-label={dark ? "Use light mode" : "Use dark mode"} onClick={() => setDark(!dark)}>
          {dark ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </header>

      {mobileMenuOpen ? (
        <div className="mobile-menu">
          {navigation.slice(5).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={() => setMobileMenuOpen(false)}>
              <Icon size={19} /> {label}
            </NavLink>
          ))}
        </div>
      ) : null}

      <main className="main-content">
        <div className="desktop-tools">
          <button className="icon-button" type="button" aria-label={dark ? "Use light mode" : "Use dark mode"} onClick={() => setDark(!dark)}>
            {dark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button className="capture-button" type="button" onClick={onCapture}>
            <Plus size={18} /> Capture
          </button>
        </div>
        {children}
      </main>

      <button className="mobile-capture-fab" type="button" aria-label="Capture" title="Capture" onClick={onCapture}>
        <Plus size={22} />
      </button>

      <nav className="bottom-nav" aria-label="Mobile navigation">
        {mobileNavigation.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end}>
            <Icon size={21} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
